import React from "react";
import { Card, CircularProgress, Tooltip } from "@material-ui/core";
import { PrimaryText } from "components/Common/PrimaryText";
import InfoTooltip from "./InfoTooltip";
import { useDisplay } from "context/DisplayContext";

const CohortHeatmap = ({ cohortData, loading, error }) => {
  const { isMobile } = useDisplay();

  if (loading) {
    return (
      <Card style={{ padding: 20, textAlign: "center", height: isMobile ? "auto" : 400 }}>
        <CircularProgress />
        <PrimaryText>Loading cohort data...</PrimaryText>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ padding: 20, textAlign: "center", height: isMobile ? "auto" : 400 }}>
        <PrimaryText color="error">
          Error loading cohort data: {error}
        </PrimaryText>
      </Card>
    );
  }

  // Show message if no cohort data
  if (!cohortData || cohortData.length === 0) {
    return (
      <Card style={{ padding: 20, textAlign: "center", height: isMobile ? "auto" : 400 }}>
        <PrimaryText style={{ color: "#666" }}>
          Cohort data unavailable
        </PrimaryText>
        <PrimaryText fontSize={12} style={{ color: "#999", marginTop: 8 }}>
          Not enough data to show cohort analysis
        </PrimaryText>
      </Card>
    );
  }

  // Format data for heatmap
  const formatCohortData = () => {
    if (!cohortData || cohortData.length === 0) return [];

    // Get retention periods
    const periods = ["day1Retention", "day7Retention", "day30Retention"];

    // Create matrix
    const matrix = cohortData.map((cohort) => {
      return {
        week: cohort.cohortWeek,
        values: periods.map((period) => cohort[period] || 0),
      };
    });

    return matrix;
  };

  const matrix = formatCohortData();
  const periods = ["Day 1", "Day 7", "Day 30"];

  // Insights for each retention period
  const getInsight = (period, value) => {
    const percentage = value * 100;

    if (period === "Day 1") {
      if (percentage >= 50)
        return "Excellent! Users find immediate value in your app.";
      if (percentage >= 30) return "Good onboarding experience.";
      if (percentage >= 10)
        return "Consider improving first-day user experience.";
      return "Critical: Users are not finding value immediately.";
    }

    if (period === "Day 7") {
      if (percentage >= 40) return "Outstanding weekly retention!";
      if (percentage >= 20) return "Good weekly engagement.";
      if (percentage >= 10) return "Moderate retention - room for improvement.";
      return "Low weekly retention - investigate user drop-off reasons.";
    }

    if (period === "Day 30") {
      if (percentage >= 30) return "Excellent long-term retention!";
      if (percentage >= 15) return "Good monthly stickiness.";
      if (percentage >= 5) return "Average monthly retention.";
      return "Poor long-term retention - focus on user engagement strategies.";
    }

    return "";
  };

  // Get color based on retention rate
  const getColor = (value) => {
    if (value >= 0.5) return "#4caf50"; // Green
    if (value >= 0.3) return "#ffeb3b"; // Yellow
    if (value >= 0.1) return "#ff9800"; // Orange
    return "#f44336"; // Red
  };

  return (
    <Card
      style={{
        padding: isMobile ? 12 : 20,
        height: isMobile ? "auto" : 400,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 20,
          flexShrink: 0,
        }}
      >
        <PrimaryText fontSize={isMobile ? 16 : 20} fontWeight={700}>
          Cohort Retention Analysis
        </PrimaryText>
        <InfoTooltip
          title="How to read this heatmap"
          content="Tracks user groups by signup week to compare retention patterns. Look for improving trends in recent cohorts."
        />
      </div>

      {matrix.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <PrimaryText>No cohort data available</PrimaryText>
        </div>
      ) : (
        <div style={{ overflow: "auto", flex: 1, width: "100%" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isMobile ? 280 : undefined }}>
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: isMobile ? "6px 4px" : "8px",
                    borderBottom: "1px solid #ddd",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "white",
                    zIndex: 1,
                  }}
                >
                  <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={600}>
                    Cohort Week
                  </PrimaryText>
                </th>
                {periods.map((period, index) => (
                  <th
                    key={index}
                    style={{
                      textAlign: "center",
                      padding: isMobile ? "6px 4px" : "8px",
                      borderBottom: "1px solid #ddd",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                    }}
                  >
                    <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={600}>
                      {period}
                    </PrimaryText>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td
                    style={{ padding: isMobile ? "6px 4px" : "8px", borderBottom: "1px solid #ddd" }}
                  >
                    <PrimaryText fontSize={isMobile ? 12 : 14}>{row.week}</PrimaryText>
                  </td>
                  {row.values.map((value, colIndex) => {
                    const period = periods[colIndex];
                    const insight = getInsight(period, value);

                    return (
                      <td
                        key={colIndex}
                        style={{
                          textAlign: "center",
                          padding: isMobile ? "6px 4px" : "8px",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        <Tooltip
                          title={
                            <div style={{ padding: 4 }}>
                              <div style={{ fontSize: 12, fontWeight: 600 }}>
                                {period} Retention
                              </div>
                              <div style={{ fontSize: 11, marginTop: 2 }}>
                                {insight}
                              </div>
                            </div>
                          }
                          arrow
                          placement="top"
                        >
                          <div
                            style={{
                              backgroundColor: getColor(value),
                              color: "white",
                              borderRadius: "4px",
                              padding: isMobile ? "3px 4px" : "4px 8px",
                              display: "inline-block",
                              minWidth: isMobile ? "40px" : "50px",
                              cursor: "help",
                            }}
                          >
                            <PrimaryText
                              fontSize={isMobile ? 12 : 14}
                              style={{ color: "white" }}
                            >
                              {value ? (value * 100).toFixed(1) + "%" : "0%"}
                            </PrimaryText>
                          </div>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default CohortHeatmap;
